import React from "react";
import { useSelector } from "react-redux";
import { getComponentUserStatus, getToolsCount } from "../store/tools";

export default function ToolList(props) {
    const { handleToolShowHide, compList, activeToolId } = props;
    const toolsCountSelector = useSelector(getToolsCount);
    const componentUserStatusSelector = useSelector(getComponentUserStatus);

    const getCount = (id) => {
        if (!toolsCountSelector) return 0;
        const toolObj = toolsCountSelector?.filter(x => x.key === id);
        if (toolObj?.length > 0) return toolObj[0].value;
        return 0;
    };

    const getBadge = (id) => {
        const compObj = (componentUserStatusSelector || []).filter(x => x.compId === id);
        if (compObj.length > 0) {
            const unreadComps = String(compObj[0].unreadCompItems || "").split(',').sort();
            const readComps = String(compObj[0].compItems || "").split(',').sort();
            let unReadCount = 0;
            unreadComps.filter(x => x !== "").forEach(x => {
                const rs = readComps.filter(y => y === x);
                if (rs.length === 0) {
                    unReadCount += 1;
                }
            });

            if (unReadCount === 0) {
                const count = getCount(id);
                return (
                    <span className="rt-tool-card-badge is-read">
                        {count}
                    </span>
                );
            } else {
                return (
                    <span className="rt-tool-card-badge is-unread">
                        {unReadCount}
                    </span>
                );
            }
        } else {
            const count = getCount(id);
            return (
                <span className="rt-tool-card-badge is-read">
                    {count}
                </span>
            );
        }
    };

    const getUnreadCount = (id) => {
        const compObj = (componentUserStatusSelector || []).filter(x => x.compId === id);
        if (compObj.length === 0) return getCount(id);

        const unreadComps = String(compObj[0].unreadCompItems || "").split(',').sort();
        const readComps = String(compObj[0].compItems || "").split(',').sort();
        let unReadCount = 0;

        unreadComps.filter(x => x !== "").forEach(x => {
            const rs = readComps.filter(y => y === x);
            if (rs.length === 0) {
                unReadCount += 1;
            }
        });

        return unReadCount;
    };

    const getToolTheme = (id) => {
        switch (id) {
            case 9: return "messages";
            case 10: return "phone";
            case 11: return "schedule";
            case 18: return "forms";
            case 24: return "templates";
            case 49: return "reports";
            default: return "default";
        }
    };

    const getToolIcon = (id) => {
        switch (id) {
            case 9: return "fa-comments-o";
            case 10: return "fa-phone";
            case 11: return "fa-calendar";
            case 18: return "fa-list-alt";
            case 24: return "fa-clone";
            case 49: return "fa-file-word-o";
            default: return "fa-wrench";
        }
    };

    const getToolMeta = (id) => {
        const count = getCount(id);
        const unreadCount = getUnreadCount(id);

        if (unreadCount > 0) return `${unreadCount} unread`;
        if (count > 0) return `${count} item${count === 1 ? "" : "s"}`;
        return "";
    };

    if (!compList || compList.length === 0) {
        return (
            <div className="rt-tools-empty">
                <div className="rt-tools-empty-icon">
                    <i className="fa fa-wrench" aria-hidden="true" />
                </div>
                <div>
                    <p className="rt-tools-empty-title">No review tools available</p>
                    <p className="rt-tools-empty-desc">Tools will appear here when enabled for this task.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rt-tools-grid">
            <style>{`
                .rt-tools-grid {
                  display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
                  gap: 8px;
                  margin-bottom: 0px !important;
                  width: 100%;
                }

                @media (min-width: 1200px) {
                  .rt-tools-grid {
                    grid-template-columns: repeat(6, 1fr);
                  }
                }

                .rt-tool-card {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  width: 100%;
                  min-height: 44px;
                  padding: 6px 10px;
                  background: #f2f4f7;
                  border: 1px solid #e5e7eb;
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  position: relative;
                  box-shadow: none;
                  user-select: none;
                  text-align: left;
                  appearance: none;
                  outline: none;
                }

                .rt-tool-card:hover {
                  background: #eaecf0;
                  border-color: #d0d5dd;
                  transform: translateY(-1px);
                }

                .rt-tool-card.active {
                  background: #ffffff;
                  border-color: #2563eb;
                  box-shadow: 0 3px 10px rgba(37, 99, 235, 0.12);
                }

                .rt-tool-card-icon-box {
                  width: 32px;
                  height: 32px;
                  border-radius: 6px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 13px;
                  background: #ffffff;
                  color: #0f172a;
                  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
                  flex-shrink: 0;
                  transition: all 0.2s ease;
                }

                .rt-tool-card.active .rt-tool-card-icon-box {
                  background: #2563eb;
                  color: #ffffff;
                }

                .rt-tool-card-info {
                  flex: 1;
                  min-width: 0;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  gap: 1px;
                }

                .rt-tool-card-name {
                  font-size: 12.5px;
                  font-weight: 600;
                  color: #1e293b;
                  margin: 0;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  line-height: 1.2;
                }

                .rt-tool-card-meta {
                  display: block;
                  color: #475569;
                  font-size: 11px;
                  font-weight: 400;
                  line-height: 1.2;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                .rt-tool-card-badge {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 10px;
                  font-weight: 700;
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  flex-shrink: 0;
                }

                .rt-tool-card-badge.is-unread {
                  background: #dc2626;
                  color: #ffffff !important;
                }

                .rt-tool-card-badge.is-read {
                  background: #059669;
                  color: #ffffff !important;
                }

                .rt-tool-card-badge.is-zero {
                  background: #cbd5e1;
                  color: #475569 !important;
                }

                .rt-tool-card-arrow {
                  color: #cbd5e1;
                  font-size: 12px;
                  flex-shrink: 0;
                  transition: all 0.2s ease;
                }

                .rt-tool-card:hover .rt-tool-card-arrow,
                .rt-tool-card.active .rt-tool-card-arrow {
                  color: #94a3b8;
                }
            `}</style>
            {compList.map((r) => {
                const isActive = activeToolId === r.id;
                const theme = getToolTheme(r.id);
                const icon = getToolIcon(r.id);
                const meta = getToolMeta(r.id);

                return (
                    <button
                        type="button"
                        key={r.id}
                        className={`rt-tool-card ${isActive ? "active" : ""}`}
                        data-theme={theme}
                        onClick={() => handleToolShowHide(r.id, "")}
                        title={r.name}
                    >
                        <div className="rt-tool-card-icon-box">
                            <i className={`fa ${icon}`} aria-hidden="true" />
                        </div>
                        <div className="rt-tool-card-info">
                            <p className="rt-tool-card-name">{r.name}</p>
                            {meta ? <span className="rt-tool-card-meta">{meta}</span> : null}
                        </div>
                        {getBadge(r.id)}
                        <i className="fa fa-angle-right rt-tool-card-arrow" aria-hidden="true" />
                    </button>
                );
            })}
        </div>
    );
}


